/**
 * Inline Select System
 * Handles responsive click-to-edit select functionality
 */

import { lockBodyScroll, unlockBodyScroll } from '../modules/scroll-lock.js';

document.addEventListener('DOMContentLoaded', function() {
    initializeInlineSelects();
});

function initializeInlineSelects() {
    // Handle mobile triggers
    document.addEventListener('click', function(e) {
        if (e.target.closest('.inline-select-trigger-mobile')) {
            const trigger = e.target.closest('.inline-select-trigger-mobile');
            const settingName = trigger.dataset.setting;
            showMobileOverlay(settingName);
        }
    });

    // Handle desktop triggers
    document.addEventListener('click', function(e) {
        if (e.target.closest('.inline-select-trigger-desktop')) {
            const trigger = e.target.closest('.inline-select-trigger-desktop');
            const settingName = trigger.dataset.setting;
            showDesktopDropdown(settingName, trigger);
        }
    });

    // Handle option selection
    document.addEventListener('click', function(e) {
        if (e.target.closest('.inline-select-option')) {
            const option = e.target.closest('.inline-select-option');
            const value = option.dataset.value;
            const label = option.dataset.label;
            const container = option.closest('.inline-select-container');
            
            selectOption(container, value, label);
            closeAllSelects();
        }
    });

    // Handle close buttons
    document.addEventListener('click', function(e) {
        if (e.target.closest('.inline-select-close')) {
            closeAllSelects();
        }
    });

    // Handle clicks outside
    document.addEventListener('click', function(e) {
        if (!e.target.closest('.inline-select-container')) {
            closeAllSelects();
        }
    });

    // Handle escape key
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
            closeAllSelects();
        }
    });
}

function showMobileOverlay(settingName) {
    closeAllSelects();
    const overlay = document.querySelector(`[data-overlay="${settingName}"]`);
    if (overlay) {
        overlay.classList.remove('hidden');
        // Prevent body scroll
        lockBodyScroll();
    }
}

function showDesktopDropdown(settingName, trigger) {
    closeAllSelects();
    const dropdown = document.querySelector(`[data-dropdown="${settingName}"]`);
    if (dropdown) {
        // Store trigger reference for repositioning on scroll
        dropdown._trigger = trigger;
        dropdown._settingName = settingName;
        
        // Position and show dropdown
        positionDropdown(dropdown, trigger);
        
        // Add scroll listener for repositioning
        window.addEventListener('scroll', handleDropdownScroll, { passive: true });
        window.addEventListener('resize', handleDropdownScroll, { passive: true });
    }
}

function positionDropdown(dropdown, trigger) {
    // Make dropdown visible but positioned off-screen to measure
    dropdown.style.visibility = 'hidden';
    dropdown.classList.remove('hidden');
    
    // Position the dropdown
    const rect = trigger.getBoundingClientRect();
    
    dropdown.style.position = 'fixed';
    dropdown.style.top = (rect.bottom + 4) + 'px';
    dropdown.style.left = rect.left + 'px';
    
    // Get dropdown dimensions after positioning
    const dropdownRect = dropdown.getBoundingClientRect();
    
    // Adjust if dropdown goes off screen
    if (dropdownRect.right > window.innerWidth) {
        dropdown.style.left = (window.innerWidth - dropdownRect.width - 10) + 'px';
    }
    
    if (dropdownRect.bottom > window.innerHeight) {
        dropdown.style.top = (rect.top - dropdownRect.height - 4) + 'px';
    }
    
    // Make visible
    dropdown.style.visibility = 'visible';
}

function handleDropdownScroll() {
    // Reposition all visible dropdowns
    const visibleDropdowns = document.querySelectorAll('.inline-select-dropdown-desktop:not(.hidden)');
    visibleDropdowns.forEach(dropdown => {
        if (dropdown._trigger) {
            positionDropdown(dropdown, dropdown._trigger);
        }
    });
}

function selectOption(container, value, label) {
    // Update hidden input
    const hiddenInput = container.querySelector('.inline-select-input');
    if (hiddenInput) {
        hiddenInput.value = value;
    }
    
    // Update display text
    const currentTexts = container.querySelectorAll('.inline-select-current-text');
    currentTexts.forEach(text => {
        text.textContent = label;
    });
    
    // Update selected state in options
    const options = container.querySelectorAll('.inline-select-option');
    options.forEach(option => {
        if (option.dataset.value === value) {
            option.classList.add('bg-sky-50', 'text-accent-text', 'font-medium');
        } else {
            option.classList.remove('bg-sky-50', 'text-accent-text', 'font-medium');
        }
    });
    
    // Trigger change event for form validation/handling
    const changeEvent = new CustomEvent('inlineSelectChange', {
        detail: { name: container.dataset.name, value: value, label: label }
    });
    container.dispatchEvent(changeEvent);
}

function closeAllSelects() {
    // Close mobile overlays and track if any were open
    let hadOpenOverlay = false;
    const overlays = document.querySelectorAll('.inline-select-overlay-mobile');
    overlays.forEach(overlay => {
        if (!overlay.classList.contains('hidden')) {
            hadOpenOverlay = true;
        }
        overlay.classList.add('hidden');
    });

    // Close desktop dropdowns
    const dropdowns = document.querySelectorAll('.inline-select-dropdown-desktop');
    dropdowns.forEach(dropdown => {
        dropdown.classList.add('hidden');
        // Reset positioning styles
        dropdown.style.top = '';
        dropdown.style.left = '';
        dropdown.style.position = '';
        dropdown.style.visibility = '';
    });

    // Only restore body scroll if a mobile overlay was actually open
    if (hadOpenOverlay) {
        unlockBodyScroll();
    }
}

// Export functions for potential external use
window.InterPals = window.InterPals || {};
window.InterPals.inlineSelect = {
    initializeInlineSelects,
    showDesktopDropdown,
    selectOption,
    closeAllSelects
}; 